import type { RuleFinding } from "@/types";

/**
 * Shared findings table (extracted from GateDemoPage's GateTable).
 * Reused on the Gate demo, Excel-upload validation, and Deal workspace screens
 * so the severity/rule/message/reference layout stays consistent (no duplication).
 *
 * Severity color tokens come from .badge.{BLOCK|ERROR|WARN|INFO} in global.css.
 */
export function FindingsTable({
  findings,
  emptyText = "발견된 이슈 없음.",
}: {
  findings: RuleFinding[];
  emptyText?: string;
}): JSX.Element {
  return (
    <table className="findings">
      <thead>
        <tr>
          <th>등급</th>
          <th>룰</th>
          <th>메시지</th>
          <th>근거</th>
        </tr>
      </thead>
      <tbody>
        {findings.length === 0 && (
          <tr>
            <td colSpan={4} className="muted">
              {emptyText}
            </td>
          </tr>
        )}
        {findings.map((f) => (
          <tr key={f.id}>
            <td>
              <span className={`badge ${f.severity}`}>{f.severity}</span>
            </td>
            <td>
              <code>{f.rule_id}</code>
            </td>
            <td>{f.message}</td>
            <td className="muted">{f.reference ?? "—"}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/**
 * Gate summary pills + pass/fail badge (shared by Gate demo + Deal workspace).
 */
export function GateSummary({
  blocks,
  errors,
  warns,
  infos,
  passed,
}: {
  blocks: number;
  errors: number;
  warns: number;
  infos: number;
  passed: boolean;
}): JSX.Element {
  return (
    <div className="gate-summary">
      <span className="pill">
        BLOCK <b>{blocks}</b>
      </span>
      <span className="pill">
        ERROR <b>{errors}</b>
      </span>
      <span className="pill">
        WARN <b>{warns}</b>
      </span>
      <span className="pill">
        INFO <b>{infos}</b>
      </span>
      <span className={`pill gate-state ${passed ? "pass" : "fail"}`}>
        {passed ? "게이트 통과" : "게이트 차단"}
      </span>
    </div>
  );
}
